var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import * as React from 'react';
import warning from 'rc-util/lib/warning';
import toArray from 'rc-util/lib/Children/toArray';
import { INTERNAL_COL_DEFINE } from '../utils/legacyUtil';
import { EXPAND_COLUMN } from '../constant';
export function convertChildrenToColumns(children) {
    return toArray(children)
        .filter(node => React.isValidElement(node))
        .map(({ key, props }) => {
        const { children: nodeChildren } = props, restProps = __rest(props, ["children"]);
        const column = Object.assign({ key }, restProps);
        if (nodeChildren) {
            column.children = convertChildrenToColumns(nodeChildren);
        }
        return column;
    });
}
function flatColumns(columns) {
    return columns.reduce((list, column) => {
        const { fixed } = column;
        // Convert `fixed='true'` to `fixed='left'` instead
        const parsedFixed = fixed === true ? 'left' : fixed;
        const subColumns = column.children;
        if (subColumns && subColumns.length > 0) {
            return [
                ...list,
                ...flatColumns(subColumns).map(subColum => (Object.assign({ fixed: parsedFixed }, subColum)))
            ];
        }
        return [
            ...list,
            Object.assign(Object.assign({}, column), { fixed: parsedFixed })
        ];
    }, []);
}
function warningFixed(flattenColumns) {
    let allFixLeft = true;
    for (let i = 0; i < flattenColumns.length; i += 1) {
        const col = flattenColumns[i];
        if (allFixLeft && col.fixed !== 'left') {
            allFixLeft = false;
        }
        else if (!allFixLeft && col.fixed === 'left') {
            warning(false, `Index ${i - 1} of \`columns\` missing \`fixed='left'\` prop.`);
            break;
        }
    }
    let allFixRight = true;
    for (let i = flattenColumns.length - 1; i >= 0; i -= 1) {
        const col = flattenColumns[i];
        if (allFixRight && col.fixed !== 'right') {
            allFixRight = false;
        }
        else if (!allFixRight && col.fixed === 'right') {
            warning(false, `Index ${i + 1} of \`columns\` missing \`fixed='right'\` prop.`);
            break;
        }
    }
}
function revertForRtl(columns) {
    return columns.map(column => {
        const { fixed } = column, restProps = __rest(column, ["fixed"]);
        // Convert `fixed='left'` to `fixed='right'` instead
        let parsedFixed = fixed;
        if (fixed === 'left') {
            parsedFixed = 'right';
        }
        else if (fixed === 'right') {
            parsedFixed = 'left';
        }
        return Object.assign({ fixed: parsedFixed }, restProps);
    });
}
/**
 * Parse `columns` & `children` into `columns`.
 */
function useColumns({ prefixCls, columns, children, expandable, expandedKeys, getRowKey, onTriggerExpand, expandIcon, rowExpandable, expandIconColumnIndex, direction, expandRowByClick, columnWidth, fixed }, transformColumns) {
    const baseColumns = React.useMemo(() => columns || convertChildrenToColumns(children), [columns, children]);
    // ========================== Expand ==========================
    const withExpandColumns = React.useMemo(() => {
        if (expandable) {
            let cloneColumns = baseColumns.slice();
            // >>> Warning if use `expandIconColumnIndex`
            if (process.env.NODE_ENV !== 'production' && expandIconColumnIndex >= 0) {
                warning(false, '`expandIconColumnIndex` is deprecated. Please use `Table.EXPAND_COLUMN` in `columns` instead.');
            }
            // >>> Insert expand column if not exist
            if (!cloneColumns.includes(EXPAND_COLUMN)) {
                const expandColIndex = expandIconColumnIndex || 0;
                if (expandColIndex >= 0) {
                    cloneColumns.splice(expandColIndex, 0, EXPAND_COLUMN);
                }
            }
            // >>> Deduplicate additional expand column
            if (process.env.NODE_ENV !== 'production' &&
                cloneColumns.filter(c => c === EXPAND_COLUMN).length > 1) {
                warning(false, 'There exist more than one `EXPAND_COLUMN` in `columns`.');
            }
            const expandColumnIndex = cloneColumns.indexOf(EXPAND_COLUMN);
            cloneColumns = cloneColumns.filter((column, index) => column !== EXPAND_COLUMN || index === expandColumnIndex);
            // >>> Check if expand column need to fixed
            const prevColumn = baseColumns[expandColumnIndex];
            let fixedColumn;
            if ((fixed === 'left' || fixed) && !expandIconColumnIndex) {
                fixedColumn = 'left';
            }
            else if ((fixed === 'right' || fixed) && expandIconColumnIndex === baseColumns.length) {
                fixedColumn = 'right';
            }
            else {
                fixedColumn = prevColumn ? prevColumn.fixed : null;
            }
            // >>> Create expandable column
            const expandColumn = {
                [INTERNAL_COL_DEFINE]: {
                    className: `${prefixCls}-expand-icon-col`,
                    columnType: 'EXPAND_COLUMN'
                },
                title: '',
                fixed: fixedColumn,
                className: `${prefixCls}-row-expand-icon-cell`,
                width: columnWidth,
                render: (_, record, index) => {
                    const rowKey = getRowKey(record, index);
                    const expanded = expandedKeys.has(rowKey);
                    const recordExpandable = rowExpandable ? rowExpandable(record) : true;
                    const icon = expandIcon({
                        prefixCls,
                        expanded,
                        expandable: recordExpandable,
                        record,
                        onExpand: onTriggerExpand
                    });
                    if (expandRowByClick) {
                        return <span onClick={e => e.stopPropagation()}>{icon}</span>;
                    }
                    return icon;
                }
            };
            return cloneColumns.map(col => (col === EXPAND_COLUMN ? expandColumn : col));
        }
        if (process.env.NODE_ENV !== 'production' && baseColumns.includes(EXPAND_COLUMN)) {
            warning(false, '`expandable` is not config but there exist `EXPAND_COLUMN` in `columns`.');
        }
        return baseColumns.filter(col => col !== EXPAND_COLUMN);
    }, [expandable, baseColumns, getRowKey, expandedKeys, expandIcon, direction]);
    // ========================= Transform ========================
    const mergedColumns = React.useMemo(() => {
        let finalColumns = withExpandColumns;
        if (transformColumns) {
            finalColumns = transformColumns(finalColumns);
        }
        // Always provides at least one column for table display
        if (!finalColumns.length) {
            finalColumns = [
                {
                    render: () => null,
                }
            ];
        }
        return finalColumns;
    }, [transformColumns, withExpandColumns, direction]);
    // ========================== Flatten =========================
    const flattenColumns = React.useMemo(() => {
        if (direction === 'rtl') {
            return revertForRtl(flatColumns(mergedColumns));
        }
        return flatColumns(mergedColumns);
    }, [mergedColumns, direction]);
    // Only check out of production since it's waste for each render
    if (process.env.NODE_ENV !== 'production') {
        warningFixed(flattenColumns);
    }
    return [mergedColumns, flattenColumns];
}
export default useColumns;
